import { DropdownMenu as MenuPrimitive } from "radix-ui";
import { type ComponentProps, createContext, useContext, useState } from "react";
import { cn } from "./cn";
import { Icon } from "./icon";

// A menu of actions or choices behind one button. Radix gives the keyboard model (arrows, type-ahead, Escape) and focus return.

const itemClasses =
  "relative flex min-h-11 cursor-pointer items-center gap-2 rounded-sm px-2.5 py-2 text-sm outline-hidden select-none compact:min-h-8 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0";

export const DropdownMenuTrigger = MenuPrimitive.Trigger;

// The open menu is drawn in the landmark that holds its button (the top bar, the page), not in `<body>`: content outside every landmark is an
// axe `region` finding and is skipped by a screen reader that jumps from landmark to landmark.
const LandmarkContext = createContext<HTMLElement | null>(null);

/**
 * A menu button is not a modal: the page behind it stays in the reading order (a modal menu marks the whole page `aria-hidden` while it is
 * open, which hides the landmarks and the heading), and Tab or a click outside closes the menu.
 */
export function DropdownMenu({ children, ...props }: ComponentProps<typeof MenuPrimitive.Root>) {
  const [landmark, setLandmark] = useState<HTMLElement | null>(null);
  return (
    <MenuPrimitive.Root modal={false} {...props}>
      {/* A hidden marker next to the button: it finds the landmark the button sits in. */}
      <span
        hidden
        ref={(node) => {
          const found =
            node?.closest<HTMLElement>("header, nav, main, aside, [role=region]") ?? null;
          setLandmark((previous) => (previous === found ? previous : found));
        }}
      />
      <LandmarkContext.Provider value={landmark}>{children}</LandmarkContext.Provider>
    </MenuPrimitive.Root>
  );
}

export function DropdownMenuContent({
  className,
  sideOffset = 6,
  ...props
}: ComponentProps<typeof MenuPrimitive.Content>) {
  const landmark = useContext(LandmarkContext);
  return (
    <MenuPrimitive.Portal container={landmark}>
      <MenuPrimitive.Content
        sideOffset={sideOffset}
        className={cn(
          "z-[var(--lf-z-popover)] grid min-w-44 compact:gap-2 overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg",
          className,
        )}
        {...props}
      />
    </MenuPrimitive.Portal>
  );
}

export function DropdownMenuItem({
  className,
  ...props
}: ComponentProps<typeof MenuPrimitive.Item>) {
  return <MenuPrimitive.Item className={cn(itemClasses, className)} {...props} />;
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: ComponentProps<typeof MenuPrimitive.CheckboxItem>) {
  return (
    <MenuPrimitive.CheckboxItem className={cn(itemClasses, "pl-8", className)} {...props}>
      <span className="absolute left-2.5 flex size-4 items-center justify-center">
        <MenuPrimitive.ItemIndicator>
          <Icon name="tick-02" />
        </MenuPrimitive.ItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  );
}

export function DropdownMenuLabel({
  className,
  ...props
}: ComponentProps<typeof MenuPrimitive.Label>) {
  return (
    <MenuPrimitive.Label
      className={cn("px-2.5 py-1.5 text-xs font-semibold text-muted-foreground", className)}
      {...props}
    />
  );
}
